import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Google Tag Manager sur les pages publiques de connexion et d'inscription (MOKA-319) : continuité
 * du suivi de la landing (même conteneur). Règles :
 *  - production seulement (app.mokahplus.com) ; ailleurs, `?gtm=debug` active le bandeau et le
 *    dataLayer SANS charger le script Google (vérification locale) ;
 *  - /sign-in et /sign-up uniquement, jamais auth/callback ni reset-password (jetons dans l'URL) ;
 *  - adresse sans paramètre, ou seulement des paramètres de campagne : `verify` (e-mail) ou
 *    `returnUrl` (chemins internes, paramètres OAuth) ne partent jamais chez Google ;
 *  - rien avant « Accepter » (bandeau, choix mémorisé 6 mois) ; en quittant ces pages, le
 *    consentement est retiré dans le dataLayer ;
 *  - choix partagé avec la landing mokahplus.com par le cookie `mokah_consent` (MOKA-383) : un
 *    choix fait sur l'un des deux sites n'est pas redemandé sur l'autre ;
 *  - champs de formulaire : `gtm.elementId` = id du champ, jamais sa valeur.
 */
export const GTM_ID = 'GTM-TW4PSF7B';
export const GTM_HOSTS = ['app.mokahplus.com'];
export const TRACKED_PATHS = ['/sign-in', '/sign-up'] as const;
/** Paramètres admis dans l'adresse : campagne, et `gtm` (mode vérification). Tout autre coupe le suivi. */
// Paramètres qui n'identifient pas une personne et ne changent pas la page :
// campagnes (utm_*, clics publicitaires), aperçu GTM / Tag Assistant (gtm_debug, gtm_auth,
// gtm_preview…) et suivi inter-domaines depuis la landing (_gl, _ga) — MOKA-354 : sans eux,
// la page n'était jamais suivie dans l'aperçu de Google ni en arrivant de la landing.
const CAMPAIGN_PARAM = /^(utm_[a-z_]+|gclid|gbraid|wbraid|fbclid|msclkid|gtm|gtm_[a-z_]+|_gl|_ga)$/i;
export const CONSENT_KEY = 'mokah.consent.analytics';
export const CONSENT_MAX_AGE_MS = 182 * 24 * 3600 * 1000;
/**
 * Cookie partagé avec la landing (MOKA-383) : le localStorage est propre à chaque origine, la landing
 * ne voit donc pas le choix de l'app ni l'inverse. Valeur `<granted|denied>:<horodatage ms>`,
 * posé sur `Domain=mokahplus.com` (landing et app), 182 jours. Le choix le plus récent l'emporte.
 */
export const SHARED_CONSENT_COOKIE = 'mokah_consent';
export const SHARED_CONSENT_DOMAIN = 'mokahplus.com';

export type ConsentChoice = 'granted' | 'denied';
export type FormFieldEvent = 'focus' | 'change';
type StoredConsent = { v: ConsentChoice; at: number };

/** Chemin suivi pour cette adresse, ou null (page non suivie ou paramètre non admis). */
export function trackedPath(url: string): string | null {
  const [beforeHash] = url.split('#');
  const [path, query = ''] = beforeHash.split('?');
  if (!(TRACKED_PATHS as readonly string[]).includes(path)) return null;
  const params = [...new URLSearchParams(query).keys()];
  return params.every(key => CAMPAIGN_PARAM.test(key)) ? path : null;
}

function fromStorage(storage: Pick<Storage, 'getItem'> | null): StoredConsent | null {
  try {
    const raw = storage?.getItem(CONSENT_KEY);
    if (!raw) return null;
    const { v, at } = JSON.parse(raw) as { v?: string; at?: string };
    const time = at ? Date.parse(at) : NaN;
    return (v === 'granted' || v === 'denied') && !Number.isNaN(time) ? { v, at: time } : null;
  } catch {
    return null;
  }
}

/** Choix lu dans le cookie partagé (`document.cookie`), ou null. */
export function readSharedConsent(cookies: string): StoredConsent | null {
  for (const part of cookies.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name !== SHARED_CONSENT_COOKIE) continue;
    const match = /^(granted|denied):(\d{10,})$/.exec(decodeURIComponent(rest.join('=')));
    return match ? { v: match[1] as ConsentChoice, at: Number(match[2]) } : null;
  }
  return null;
}

/**
 * Choix encore valable (6 mois), sinon null : le bandeau se réaffiche. Entre l'app (localStorage)
 * et le cookie partagé avec la landing, le plus récent l'emporte.
 */
export function readConsent(storage: Pick<Storage, 'getItem'> | null, now = Date.now(), cookies = ''): ConsentChoice | null {
  const latest = [fromStorage(storage), readSharedConsent(cookies)]
    .filter((c): c is StoredConsent => c !== null && now - c.at <= CONSENT_MAX_AGE_MS)
    .sort((a, b) => b.at - a.at)[0];
  return latest?.v ?? null;
}

/** Attributs du cookie partagé : tout mokahplus.com en production, l'hôte seul ailleurs (local). */
export function sharedConsentCookie(choice: ConsentChoice, at: number, host: string, https: boolean): string {
  const shared = host === SHARED_CONSENT_DOMAIN || host.endsWith(`.${SHARED_CONSENT_DOMAIN}`);
  return [
    `${SHARED_CONSENT_COOKIE}=${choice}:${at}`,
    ...(shared ? [`Domain=${SHARED_CONSENT_DOMAIN}`] : []),
    'Path=/',
    `Max-Age=${Math.floor(CONSENT_MAX_AGE_MS / 1000)}`,
    'SameSite=Lax',
    ...(https ? ['Secure'] : []),
  ].join('; ');
}

type DataLayerWindow = Window & { dataLayer?: unknown[] };

@Injectable({ providedIn: 'root' })
export class GtmService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly window = this.document.defaultView as DataLayerWindow | null;

  private readonly debug = signal(false);
  private readonly host = this.window?.location.hostname ?? '';
  /** Le suivi peut-il exister ici (production, ou vérification locale `?gtm=debug`) ? */
  readonly available = computed(() => GTM_HOSTS.includes(this.host) || this.debug());

  readonly choice = signal<ConsentChoice | null>(readConsent(this.storage(), Date.now(), this.cookies()));
  /** Page courante suivie (chemin), ou null. */
  readonly page = signal<string | null>(null);
  readonly bannerVisible = computed(() => this.available() && this.page() !== null && this.choice() === null);

  private loaded = false;
  private granted = false;

  constructor() {
    try {
      const params = new URLSearchParams(this.window?.location.search ?? '');
      if (params.get('gtm') === 'debug') this.window?.sessionStorage.setItem('mokah.gtm.debug', '1');
      this.debug.set(this.window?.sessionStorage.getItem('mokah.gtm.debug') === '1');
    } catch {
      this.debug.set(false);
    }

    const sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.onNavigation(e.urlAfterRedirects));
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
    // Service créé après la première navigation (page déjà affichée) : on évalue l'adresse actuelle.
    if (this.router.navigated) this.onNavigation(this.router.url);
  }

  accept(): void {
    this.save('granted');
    const page = this.page();
    if (page) this.start(page);
  }

  refuse(): void {
    this.save('denied');
    this.revoke();
  }

  /** « Gérer les cookies » : rouvre le bandeau. */
  reopen(): void {
    this.choice.set(null);
  }

  /**
   * Conversion « inscription » (MOKA-383) : formulaire accepté (champs valides, conditions cochées,
   * compte créé, e-mail envoyé), avant l'écran du code. Seulement avec consentement, sur une page suivie.
   */
  signUp(method: 'email'): void {
    if (!this.granted || !this.page()) return;
    this.push({ event: 'sign_up', method });
  }

  /** Interaction avec un champ : id du champ seulement, jamais sa valeur. */
  fieldEvent(kind: FormFieldEvent, element: EventTarget | null, formId: string): void {
    if (!this.granted || !this.page()) return;
    const el = element as HTMLElement | null;
    if (!el?.id || !['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) return;
    this.push({ event: `form_field_${kind}`, 'gtm.elementId': el.id, form_id: formId, page_path: this.page() });
  }

  private onNavigation(url: string): void {
    const page = this.available() ? trackedPath(url) : null;
    this.page.set(page);
    if (page && this.choice() === 'granted') this.start(page);
    else this.revoke();
  }

  private start(page: string): void {
    const w = this.window;
    if (!w || !this.available()) return;
    w.dataLayer = w.dataLayer ?? [];
    // Consent Mode v2 (MOKA-354) : défaut refusé, PUIS accord, PUIS démarrage du conteneur.
    // L'accord doit précéder `gtm.js` : sinon les balises « Initialisation » / « Toutes les pages »
    // partent encore sous consentement refusé (GA4 sans cookie, pages vues mal comptées).
    // Dans Tag Assistant, « On-page Default » reste donc « Denied » : c'est normal, seul
    // « Current State » compte.
    if (!this.loaded) {
      this.gtag('consent', 'default', {
        ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
      });
    }
    if (!this.granted) {
      this.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted' });
      this.granted = true;
    }
    if (!this.loaded) {
      this.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      if (!this.debug()) {
        const script = this.document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
        this.document.head.appendChild(script);
      }
      this.loaded = true;
    }
    this.push({ event: 'page_view', page_path: page, page_location: `${w.location.origin}${page}`, page_title: this.document.title });
  }

  /** Quitter les pages suivies ou refuser : plus aucune balise autorisée. */
  private revoke(): void {
    if (!this.loaded || !this.granted) return;
    this.gtag('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    this.granted = false;
  }

  private save(choice: ConsentChoice): void {
    this.choice.set(choice);
    const now = new Date();
    try {
      this.storage()?.setItem(CONSENT_KEY, JSON.stringify({ v: choice, at: now.toISOString() }));
    } catch {
      // Stockage indisponible (navigation privée) : le choix vaut pour la session.
    }
    try {
      this.document.cookie = sharedConsentCookie(choice, now.getTime(), this.host, this.window?.location.protocol === 'https:');
    } catch {
      // Cookies bloqués : le choix reste propre à l'app.
    }
  }

  private cookies(): string {
    try {
      return this.document.cookie ?? '';
    } catch {
      return '';
    }
  }

  private storage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }

  private push(entry: Record<string, unknown>): void {
    this.window?.dataLayer?.push(entry);
  }

  // Forme exigée par GTM pour les commandes de consentement : un objet `arguments`, pas un tableau.
  private gtag(..._args: unknown[]): void {
    this.window?.dataLayer?.push(arguments);
  }
}
